import './reset.scss'
import { ThemeProvider } from '@emotion/react'
import { createTheme } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import Cookies from 'js-cookie'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'

import ClarityProvider from 'clarity'
import NoSessionPage from 'error'
import { GetAuthSession } from 'hooks/axios'
import { SnackbarProvider } from 'hooks/useSnackbar/SnackbarContext'
import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY } from 'i18n/constants'
import Layout from 'layout'
import AssignDetailPage from 'pages/assign/detail/page'
import AssignHistoryPage from 'pages/assign/history/page'
import AssignPage from 'pages/assign/page'
import LoginPage from 'pages/login/page'
import RedirectPage from 'pages/redirect/page'
import TopPage from 'pages/top/page'
import { insightsRoutes } from 'routes/insight'
import { notificationsRoutes } from 'routes/notification'
import { securityReportRoutes } from 'routes/security-report'
import { settingsRoutes } from 'routes/setting'
import { shiftsRoutes } from 'routes/shift'
import { PRIMARY_COLOR } from 'valiable'

import NotFoundPage from './notFound'

const createAppTheme = (language: string) =>
  createTheme({
    palette: {
      primary: {
        main: PRIMARY_COLOR,
        light: PRIMARY_COLOR,
        dark: PRIMARY_COLOR,
        contrastText: '#ffffff',
      },
      secondary: {
        main: PRIMARY_COLOR,
      },
      warning: {
        main: '#ff4800',
      },
      error: {
        main: '#ff005a',
      },
      action: {
        hover: '#edebeb',
      },
    },
    typography: {
      fontSize: 12,
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            padding: '6px 8px',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            padding: '6px 8px',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '8px 8px',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '0 0 10px',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            [`& .MuiOutlinedInput-root`]: {
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              paddingLeft: '0 !important',
            },
          },
          input: {
            minHeight: '30px',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            ...(language !== 'ja' && { lineHeight: 1.2 }),
          },
        },
      },
    },
  })

const isAuthenticated = (): boolean => {
  const sessionKey = GetAuthSession()
  if (!!sessionKey) {
    return true
  } else {
    return false
  }
}

function App() {
  const { i18n } = useTranslation()
  const theme = useMemo(() => createAppTheme(i18n.language), [i18n.language])

  // Flutter WebViewからの言語切り替えメッセージをリスン
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'CHANGE_LANGUAGE') {
        const language = event.data.language
        if (language && ['ja', 'en', 'vi', 'my', 'fr'].includes(language)) {
          i18n.changeLanguage(language)
        }
      }
    }

    // 別タブ(別WebView)で言語が変更された場合に同期する
    const handleFocus = () => {
      const storedLang = Cookies.get(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE
      if (storedLang && storedLang !== i18n.language) {
        i18n.changeLanguage(storedLang)
      }
    }

    window.addEventListener('message', handleMessage)
    window.addEventListener('visibilitychange', handleFocus)
    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('visibilitychange', handleFocus)
      window.removeEventListener('focus', handleFocus)
    }
  }, [i18n])

  return (
    <Router>
      <ClarityProvider />
      <ThemeProvider theme={theme}>
        <SnackbarProvider>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={i18n.language}
          >
            <Routes>
              <Route path={'/login'} element={<LoginPage />} />
              <Route path={'/redirect'} element={<RedirectPage />} />
              <Route
                path="/"
                element={isAuthenticated() ? <Layout /> : <NoSessionPage />}
              >
                <Route path={'/top'} element={<TopPage />} />
                <Route path={'/assign'} element={<AssignPage />} />
                <Route path={'/assign/detail'} element={<AssignDetailPage />} />
                <Route
                  path={'/assign/history'}
                  element={<AssignHistoryPage />}
                />
                {notificationsRoutes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={route.element}
                  />
                ))}
                {shiftsRoutes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={route.element}
                  />
                ))}
                {securityReportRoutes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={route.element}
                  />
                ))}
                {settingsRoutes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={route.element}
                  />
                ))}
                {insightsRoutes.map((route, index) => (
                  <Route
                    key={index}
                    path={route.path}
                    element={route.element}
                  />
                ))}

                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </LocalizationProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </Router>
  )
}

export default App
